// El popup de funcionalidad nueva — ADR-2026-08-31-anuncios-de-funcionalidad.
//
// Se monta UNA vez (en `ProtectedRoute`) y decide solo: mira el catálogo de
// `lib/anuncios.ts`, la ruta actual y qué anuncios ya cerró el usuario. Ninguna
// pantalla sabe que existe.
//
// La forma sale de mirar cómo lo resuelven los que lo hacen bien (en Mobbin: Semrush,
// Quicken, Lovable, Relume en escritorio; Kitchen Stories, Slack, Binance y Stake en
// teléfono). Todos coinciden en lo mismo:
//   · el VISUAL arriba, a sangre contra los bordes del modal;
//   · la cruz FLOTANDO sobre el visual, arriba a la derecha;
//   · abajo la pastilla "Nuevo", el título, una línea de texto y UN botón principal.
// Lo que no comparten es el contenido del visual, y ahí está la diferencia entre un
// anuncio que se lee y uno que se cierra: los que ponen una ilustración de stock se
// saltean, los que muestran LA COSA (Semrush muestra su input, Lovable su dashboard)
// se miran. Por eso acá el visual no es un dibujo: es la MISMA cabecera de la pantalla
// que el anuncio presenta —la tira de fotos sobre negro de `/referidos`— con el trato
// del programa dibujado encima.

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  TiraDeFotos,
  LuzAzul,
  NEGRO_REFERIDOS,
} from './referidos/TiraDeFotos';
import { anuncioVigente, marcarEstadoDelAnuncio, type Anuncio } from '../lib/anuncios';
import { isSeen, loadOnboardingSeen, markSeen } from '../lib/onboardingSeen';
import { useUserGroup } from '../hooks/useUserGroup';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { correspondeMostrar, leerEstadoDelPopup, registrarCancelacionDelPopup } from '../lib/pushPopup';
import { presentacionNotificacion } from '../lib/notificaciones';
import { PlatformLogoChip } from './PlatformLogos';

/**
 * Cabecera visual de "Invitá y ganá": la MISMA superficie que el encabezado de la
 * pantalla — la tira de fotos sobre negro con la luz azul bajando de ellas
 * (`components/referidos/TiraDeFotos`, ADR-2026-08-28-referidos-mas-visual).
 *
 * No es una decisión estética sino de continuidad: el anuncio presenta una pantalla,
 * y si su cabecera fuera otra cosa, tocar el CTA llevaría a un lugar que no se parece
 * a lo que se acaba de mirar. Compartiendo el componente, además, no pueden
 * desincronizarse — retocás la tira una vez y cambian las dos.
 *
 * Encima van las dos mitades del trato (vos +50, tu amigo +50), apoyadas sobre la
 * parte ya fundida a negro de la tira, igual que el título en la pantalla.
 */
function VisualReferidos() {
  return (
    <div
      className="relative h-[236px] overflow-hidden"
      style={{ backgroundColor: NEGRO_REFERIDOS }}
    >
      {/* 6 columnas también en teléfono: acá la tira mide 148px, no 200, y lo que
          aporta es la TEXTURA de fondo, no cada foto. Con 3 columnas a este alto las
          fotos quedan casi cuadradas y se leen como tres imágenes sueltas. */}
      <TiraDeFotos className="h-[148px]" columnasMobile={6} />
      <LuzAzul className="top-[86px] h-[150px] w-[130%] opacity-[0.34]" />

      {/* Las tarjetas se apoyan abajo, sobre el negro al que funde la tira — el mismo
          lugar donde la pantalla pone su título. Sobre las fotos no se leerían: son
          superficies translúcidas y necesitan un fondo parejo detrás. */}
      <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-center gap-3 px-6 pb-5">
        <TarjetaCredito rotulo="Vos" />
        {/* El "+" es lo que convierte dos tarjetas sueltas en un trato: sin él se
            leen como dos opciones entre las que hay que elegir. */}
        <span className="text-[22px] font-bold leading-none text-white/85">+</span>
        <TarjetaCredito rotulo="Tu amigo" />
      </div>
    </div>
  );
}

function TarjetaCredito({ rotulo }: { rotulo: string }) {
  return (
    // Sobre negro, el `bg-white/[0.14]` de antes desaparecía: la tarjeta necesita algo
    // más de cuerpo y un aro más marcado para despegarse del fondo.
    <div className="flex w-[140px] flex-col items-center gap-1 rounded-2xl bg-white/[0.09] px-4 py-3 ring-1 ring-inset ring-white/[0.18] backdrop-blur-md">
      <span className="text-[12px] font-[500] text-white/65">{rotulo}</span>
      <span className="flex items-center gap-1.5">
        <img src="/images/token_icon.png" alt="" aria-hidden="true" className="h-5 w-5" />
        <span className="text-[26px] font-bold leading-none tracking-[-0.02em] text-white">
          +50
        </span>
      </span>
      <span className="text-[11px] text-white/50">créditos</span>
    </div>
  );
}

/**
 * Cabecera de "Activá las notificaciones": tres notificaciones de Atlas apiladas,
 * dibujadas como las dibuja la campana.
 *
 * Es la misma regla que el visual de referidos —mostrar LA COSA, no una ilustración—
 * aplicada a un pedido de permiso. Mirando cómo lo resuelven los que convierten
 * (Mobbin: Claude, Peacock y Skiff apilan notificaciones falsas; Perplexity muestra
 * las tarjetas; X, Instagram y Airwallex ponen una campana genérica), la diferencia
 * está justo ahí: la campana ilustra la FUNCIÓN que le estamos pidiendo activar, y
 * las tarjetas muestran lo que la persona se lleva. Acá lo que se lleva es
 * literalmente lo que ya ve en la campana de Atlas.
 *
 * Por eso los íconos y colores salen de `presentacionNotificacion`, el mismo mapa que
 * usa `FilaNotificacion`: si mañana `imagen_lista` cambia de glifo, esta cabecera
 * cambia con él en vez de quedar mintiendo sobre cómo se ven los avisos de verdad.
 *
 * La pila se apoya abajo y el contenedor le corta la de arriba: eso es lo que dice
 * "y siguen llegando" sin escribirlo. Los textos son ejemplos plausibles, no datos
 * del usuario — pedirlos de verdad sería una consulta para una decoración.
 */
function VisualPush() {
  return (
    // 184px y no los 236 del visual de referidos: es la altura con la que la pila
    // NO entra entera y el borde superior le corta la tarjeta de atrás. Ese corte es
    // el que dice "y siguen llegando" sin escribirlo — con la pila holgada, las tres
    // tarjetas se leen como tres avisos y nada más.
    <div className="relative h-[184px] overflow-hidden bg-gradient-to-b from-[#E8EDFF] via-[#F1F4FF] to-white">
      {/* Un halo detrás de la pila, del azul de la marca. Sin él el degradé se lee
          como un fondo gris y las tarjetas blancas no tienen de dónde despegarse. */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 top-16 h-[190px] w-[320px] -translate-x-1/2 rounded-full bg-brand-primary/20 blur-3xl"
      />
      <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-2 px-5 pb-5">
        <AvisoDeMuestra
          tipo="referido_activado"
          titulo="Ganaste 50 créditos"
          cuerpo="Tu amigo activó su plan"
          cuando="hace 2 h"
          atenuado={2}
        />
        <AvisoDeMuestra
          tipo="video_listo"
          titulo="Tu video está listo"
          cuerpo="Zapatillas — 8 s"
          cuando="hace 12 min"
          atenuado={1}
        />
        {/* La de adelante va sin atenuar y con el tinte de no-leída: es la que la
            persona tiene que poder leer de un vistazo, y es la promesa del título. */}
        <AvisoDeMuestra
          tipo="imagen_lista"
          titulo="Se generaron 4 imágenes"
          cuerpo="Ya podés verlas en tu galería"
          cuando="recién"
          atenuado={0}
        />
      </div>
    </div>
  );
}

/**
 * Una notificación de mentira con la anatomía de la de verdad: casillero con el
 * glifo del tipo, título, una línea de cuerpo y el tiempo relativo.
 *
 * `atenuado` es la profundidad en la pila (0 = adelante), y se paga con opacidad y
 * ancho, nunca con solapamiento: superponerlas taparía justo el texto, que es lo
 * único que hace que esto funcione mejor que un dibujo.
 */
function AvisoDeMuestra({
  tipo,
  titulo,
  cuerpo,
  cuando,
  atenuado,
}: {
  tipo: string;
  titulo: string;
  cuerpo: string;
  cuando: string;
  atenuado: 0 | 1 | 2;
}) {
  const { icono, clases } = presentacionNotificacion(tipo);
  const alFrente = atenuado === 0;
  return (
    <div
      aria-hidden="true"
      // Cada tarjeta más atrás es más angosta. Es lo que convierte tres rectángulos
      // apilados en una pila con profundidad — y, de paso, lo que despeja la esquina
      // superior derecha: con las tres del mismo ancho, la cruz del modal se apoyaba
      // justo encima de la tarjeta cortada.
      style={{ opacity: [1, 0.72, 0.45][atenuado], maxWidth: [326, 304, 280][atenuado] }}
      className={`flex w-full items-center gap-2.5 rounded-[14px] px-3 py-2.5 ring-1 ring-inset ${
        alFrente
          ? 'bg-white shadow-[0_8px_22px_rgba(2,6,43,0.12)] ring-black/[0.05]'
          : 'bg-white/85 shadow-[0_4px_12px_rgba(2,6,43,0.06)] ring-black/[0.04]'
      }`}
    >
      <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-[10px] ${clases}`}>
        <span className="material-symbols-rounded text-[17px]">{icono}</span>
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[12.5px] font-[600] leading-[16px] text-black/85">
          {titulo}
        </span>
        <span className="mt-0.5 block truncate text-[11.5px] leading-[15px] text-black/50">
          {cuerpo}
        </span>
      </span>
      <span className="shrink-0 text-[10.5px] leading-none text-black/35">{cuando}</span>
      {/* El punto de no-leída sólo en la de adelante, igual que en la campana. */}
      <span className="w-2 shrink-0">
        {alFrente && <span className="block h-2 w-2 rounded-full bg-brand-primary" />}
      </span>
    </div>
  );
}

/**
 * Cabecera de "Lanzamientos": un lanzamiento de muestra, dibujado como la tabla de la
 * pantalla — la misma regla de mostrar LA COSA que siguen los otros dos visuales.
 *
 * La identidad sale de la sección y no de la marca: Lanzamientos es una herramienta de
 * trabajo, en neutros y con el negro del botón Publicar, así que la cabecera va sobre
 * papel cálido con una trama de puntos (una mesa de armado), no sobre el azul del de
 * push. Mirados en fila, los tres anuncios tienen que poder distinguirse de un vistazo.
 *
 * Cuenta el método entero sin texto: la tienda de destino arriba, los productos
 * tildados listos, UNO destildado que se queda afuera —lo tildado es lo que se publica,
 * ADR-2026-09-30-lo-tildado-es-lo-que-se-publica— y el aviso de que ya subió.
 *
 * La tarjeta entra ENTERA a propósito: cortarla contra el borde (como la pila del de
 * push) se comía justo la fila destildada, que es la que cuenta la regla. Y el aviso
 * cuelga DEBAJO de la tarjeta y no encima: apoyado sobre las filas tapaba los "Listo".
 */
function VisualLanzamientos() {
  return (
    <div
      aria-hidden="true"
      className="relative h-[248px] overflow-hidden bg-[#F3F1EC]"
      style={{
        backgroundImage: 'radial-gradient(rgba(20,20,20,0.09) 1px, transparent 1px)',
        backgroundSize: '14px 14px',
      }}
    >
      <div className="absolute left-1/2 top-5 w-[300px] max-w-[calc(100%-96px)] -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(20,18,12,0.10)] ring-1 ring-inset ring-black/[0.05]">
        <div className="flex items-center gap-2.5 border-b border-neutral-100 px-3.5 py-2">
          <PlatformLogoChip id="tiendanube" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-[600] leading-[16px] text-black/85">
              Colección Verano
            </span>
            <span className="block text-[11px] leading-[14px] text-black/45">
              3 productos · 7 fotos
            </span>
          </span>
          {/* El negro del botón de la pantalla, no el azul de la marca: es el mismo
              botón que la persona va a apretar. */}
          <span className="flex h-7 shrink-0 items-center gap-1 rounded-lg bg-brand-black px-2.5 text-[11px] font-[600] text-white">
            <span className="material-symbols-rounded text-[14px]">upload</span>
            Publicar
          </span>
        </div>
        <ProductoDeMuestra foto="/images/referidos/muestra-1.jpg" nombre="Campera náutica" tildado />
        <ProductoDeMuestra foto="/images/referidos/muestra-2.jpg" nombre="Campera de jean" tildado />
        <ProductoDeMuestra foto="/images/referidos/muestra-3.jpg" nombre="Vestido de lino" tildado={false} />
      </div>

      {/* El final del camino, montado sobre el borde de abajo de la tarjeta (sobre el
          padding de la última fila, no sobre su texto): sin esto el visual
          muestra la herramienta pero no lo que se lleva la persona, que es la tienda
          cargada. */}
      <div className="absolute left-1/2 top-[186px] z-20 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-white py-2 pl-2 pr-3 shadow-[0_10px_26px_rgba(20,18,12,0.16)] ring-1 ring-inset ring-black/[0.05]">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-green-50 text-green-700">
          <span className="material-symbols-rounded text-[15px]">check</span>
        </span>
        <span>
          <span className="block text-[11.5px] font-[600] leading-[14px] text-black/85">
            Publicado en tu tienda
          </span>
          <span className="block text-[10.5px] leading-[13px] text-black/45">2 productos · 5 fotos</span>
        </span>
      </div>
    </div>
  );
}

/** Una fila de la tabla de un lanzamiento: casilla, miniatura, nombre y estado. */
function ProductoDeMuestra({
  foto,
  nombre,
  tildado,
}: {
  foto: string;
  nombre: string;
  tildado: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-neutral-100 px-3.5 py-1.5 last:border-b-0">
      {/* La casilla de la tabla (`accent-brand-black`), dibujada: un input de verdad
          acá sería un control que no hace nada. */}
      <span
        className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] ${
          tildado ? 'bg-brand-black text-white' : 'border border-neutral-300 bg-white'
        }`}
      >
        {tildado && <span className="material-symbols-rounded text-[13px] font-bold">check</span>}
      </span>
      <img
        src={foto}
        alt=""
        className={`h-7 w-7 shrink-0 rounded-lg object-cover ${tildado ? '' : 'opacity-45'}`}
      />
      <span
        className={`min-w-0 flex-1 truncate text-[12px] font-[500] ${
          tildado ? 'text-black/80' : 'text-black/35'
        }`}
      >
        {nombre}
      </span>
      {tildado ? (
        <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[10.5px] font-[500] text-green-700">
          Listo
        </span>
      ) : (
        <span className="shrink-0 text-[10.5px] text-black/35">No va</span>
      )}
    </div>
  );
}

/**
 * Cabecera del calendario de Instagram: una semana del calendario, dibujada como la
 * grilla de la pantalla — portadas con su hora y el borde de color del estado — y el
 * aviso de que el agente la armó. Misma regla que los otros: mostrar LA COSA.
 *
 * El fondo toma los colores de Instagram, pálidos, para que se distinga de un vistazo
 * del papel cálido de Tienda online y del azul del de push. Un día lleva la marca ✨ del
 * agente, igual que en la grilla, y otro queda libre con el "+" punteado del horario
 * sugerido: son las dos maneras de llenar el mes.
 */
function VisualInstagram() {
  const dias: Array<{ d: string; n: number; post?: { foto: string; hora: string; tono: string; agente?: boolean } }> = [
    { d: 'Lun', n: 12 },
    { d: 'Mar', n: 13, post: { foto: '/images/referidos/muestra-2.jpg', hora: '12:00', tono: 'border-emerald-500', agente: true } },
    { d: 'Mié', n: 14 },
    { d: 'Jue', n: 15, post: { foto: '/images/referidos/muestra-4.jpg', hora: '18:30', tono: 'border-emerald-500', agente: true } },
    { d: 'Vie', n: 16 },
    { d: 'Sáb', n: 17, post: { foto: '/images/referidos/muestra-5.jpg', hora: '11:00', tono: 'border-amber-400', agente: true } },
    { d: 'Dom', n: 18 },
  ];
  return (
    <div
      aria-hidden="true"
      className="relative h-[248px] overflow-hidden bg-gradient-to-br from-[#FFF1E6] via-[#FDE8F1] to-[#EEE9FF]"
    >
      <div className="absolute left-1/2 top-5 w-[330px] max-w-[calc(100%-80px)] -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-[0_10px_30px_rgba(60,20,60,0.12)] ring-1 ring-inset ring-black/[0.05]">
        <div className="flex items-center gap-2.5 border-b border-neutral-100 px-3.5 py-2">
          <PlatformLogoChip id="instagram" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-[600] leading-[16px] text-black/85">Octubre</span>
            <span className="block text-[11px] leading-[14px] text-black/45">3 posts esta semana</span>
          </span>
          <span className="flex h-7 shrink-0 items-center gap-1 rounded-lg bg-brand-primary px-2.5 text-[11px] font-[600] text-white">
            <span className="material-symbols-rounded text-[14px]">add</span>
            Nuevo post
          </span>
        </div>
        <div className="grid grid-cols-7">
          {dias.map((x) => (
            <div key={x.d} className="flex flex-col items-center gap-1 border-r border-neutral-100 px-0.5 pb-2 pt-1.5 last:border-r-0">
              <span className="text-[9px] font-[600] uppercase tracking-wide text-black/35">{x.d}</span>
              <span className={`grid h-5 w-5 place-items-center rounded-full text-[11px] font-[600] ${x.n === 13 ? 'bg-brand-primary text-white' : 'text-black/70'}`}>
                {x.n}
              </span>
              {x.post ? (
                <span className={`relative mt-0.5 flex w-full flex-col items-center gap-0.5 rounded-md border-l-[3px] bg-neutral-50 py-1 ${x.post.tono}`}>
                  <img src={x.post.foto} alt="" className="h-8 w-8 rounded-md object-cover" />
                  <span className="text-[9px] font-[600] text-black/70">{x.post.hora}</span>
                  {x.post.agente && (
                    <span className="material-symbols-rounded absolute -right-1 -top-1.5 text-[13px] text-brand-primary">auto_awesome</span>
                  )}
                </span>
              ) : x.n === 16 ? (
                <span className="mt-0.5 grid h-[54px] w-full place-items-center rounded-md border border-dashed border-[#A5B4FC] text-[9px] font-[600] text-brand-primary">
                  + 19:00
                </span>
              ) : (
                <span className="mt-0.5 h-[54px] w-full" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* El final del camino: la semana armada por el agente, lista para revisar. */}
      <div className="absolute left-1/2 top-[176px] z-20 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-white py-2 pl-2 pr-3 shadow-[0_10px_26px_rgba(60,20,60,0.16)] ring-1 ring-inset ring-black/[0.05]">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[#EEF2FF] text-brand-primary">
          <span className="material-symbols-rounded text-[15px]">auto_awesome</span>
        </span>
        <span>
          <span className="block whitespace-nowrap text-[11.5px] font-[600] leading-[14px] text-black/85">
            El agente armó tu semana
          </span>
          <span className="block whitespace-nowrap text-[10.5px] leading-[13px] text-black/45">3 posts listos para revisar</span>
        </span>
      </div>
    </div>
  );
}

const VISUALES: Record<Anuncio['visual'], () => JSX.Element> = {
  referidos: VisualReferidos,
  push: VisualPush,
  lanzamientos: VisualLanzamientos,
  instagram: VisualInstagram,
};

export function AnuncioModal() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  /**
   * La búsqueda (`?…`) con la que se LLEGÓ a esta ruta. Se anota en el render en que
   * cambia la ruta, antes de cualquier efecto: la pantalla de destino puede limpiar su
   * parámetro enseguida (Lanzamientos borra `?nuevo=1` al leerlo), y este modal decide
   * recién cuando termina de mirar el estado de push — leer `search` en ese momento
   * daría la URL ya limpia y el anuncio saldría encima del modal que el parámetro abrió.
   */
  const llegadaRef = useRef({ pathname, search });
  if (llegadaRef.current.pathname !== pathname) llegadaRef.current = { pathname, search };
  const { isDevTester } = useUserGroup();
  const { soporte, permiso, suscripto, cargando: cargandoPush, activar } = usePushNotifications();
  const [anuncio, setAnuncio] = useState<Anuncio | null>(null);
  const [visible, setVisible] = useState(false);
  const [activando, setActivando] = useState(false);
  const [errorPush, setErrorPush] = useState<string | null>(null);
  const userIdRef = useRef<string | null>(null);
  const planRef = useRef<string | null>(null);
  const cerrarRef = useRef<HTMLButtonElement>(null);
  /**
   * Un cierre que NO cuenta como "ahora no". Se prende cuando activar falló por algo
   * nuestro (backend caído, service worker que no levanta): el calendario de
   * reintentos tiene que medir decisiones de la persona, no caídas del sistema. Sin
   * esto, un backend con problemas le gastaría un escalón a todo el mundo a la vez.
   */
  const sinContarRef = useRef(false);
  /**
   * El turno de esta pantalla. Se renueva en cada cambio de ruta y se consume al
   * mostrar un anuncio: es lo que sostiene "nunca dos seguidos". Sin él, al cerrar el
   * de push el efecto vuelve a correr (cambió el estado del permiso) y el de
   * referidos aparecería pisándole la salida.
   */
  const turnoRef = useRef(true);
  /** Espejo de `anuncio` para los efectos que no lo tienen en sus dependencias. */
  const anuncioRef = useRef<Anuncio | null>(null);
  anuncioRef.current = anuncio;

  useEffect(() => {
    turnoRef.current = true;
    // Ruta nueva: hasta que el efecto de abajo decida, los recorridos esperan. Si ya
    // hay un anuncio en pantalla (se navegó con él abierto), sigue `mostrando`.
    if (!anuncioRef.current) marcarEstadoDelAnuncio('decidiendo');
  }, [pathname]);

  /**
   * ¿Este DISPOSITIVO está en condiciones de recibir el pedido? `undefined` mientras
   * se averigua. Es sólo el estado del navegador: el calendario de reintentos NO
   * entra acá (ver abajo, en el efecto).
   *
   * Las cuatro razones para no pedirlo son distintas entre sí y todas terminan en lo
   * mismo: no hay nada que la persona pueda decidir acá. Sin soporte no hay push;
   * en iPhone sin la PWA instalada tampoco (y la instrucción para instalarla ya vive
   * en Configuración, que es donde tiene contexto); con el permiso ya dado o ya
   * bloqueado, el navegador decidió y este popup no puede cambiarlo.
   */
  const puedePedirPush = useMemo<boolean | undefined>(() => {
    if (cargandoPush) return undefined;
    if (soporte !== 'ok') return false;
    if (permiso !== 'default') return false;
    if (suscripto) return false;
    return true;
  }, [cargandoPush, soporte, permiso, suscripto]);

  useEffect(() => {
    // Sin el estado del navegador no se decide nada: elegir ahora significaría
    // descartar el pedido de push por no saber todavía si corresponde, y mostrar otro
    // anuncio en su lugar. Es una espera de milisegundos.
    if (cargandoPush || !turnoRef.current) return;

    let vigente = true;
    void (async () => {
      const { data } = await supabase.auth.getUser();
      const uid = data?.user?.id;
      if (!vigente) return;
      if (!uid) {
        marcarEstadoDelAnuncio('libre');
        return;
      }
      userIdRef.current = uid;

      // El "ya lo vi" vive en la DB (`profiles.onboarding_seen`), así que hay que
      // AWAITearlo antes de decidir: sin esto, `isSeen` devuelve false mientras carga
      // y el anuncio parpadea en pantalla para alguien que ya lo había cerrado.
      await loadOnboardingSeen(uid);
      if (!vigente) return;

      if (planRef.current === null) {
        const { data: perfil } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', uid)
          .maybeSingle();
        const idCompany = perfil?.id_company as string | undefined;
        if (idCompany) {
          const { data: empresa } = await supabase
            .from('company_profiles')
            .select('plan')
            .eq('id_company', idCompany)
            .maybeSingle();
          planRef.current = (empresa?.plan as string | undefined) ?? null;
        }
      }
      if (!vigente) return;

      // El calendario se lee ACÁ, en cada selección, y no en un `useMemo`: cerrar con
      // "Ahora no" sólo escribe en `localStorage` y no cambia ninguna dependencia del
      // memo (el permiso sigue en `default`, sigue sin suscripción). Memoizado, el
      // valor quedaba en `true` para toda la sesión y la siguiente navegación —que
      // renueva el turno— volvía a mostrar el pedido al instante, salteándose la
      // espera de 3, 7, 21 y 45 días. Leerlo tarde, además de correcto, es lo que
      // corresponde: entre el turno y esta línea hubo awaits.
      const pushPendiente =
        puedePedirPush === true && correspondeMostrar(leerEstadoDelPopup(), new Date());

      const elegido = anuncioVigente({
        ruta: pathname,
        busqueda: llegadaRef.current.search,
        plan: planRef.current,
        esDevTester: isDevTester,
        yaVisto: (id) => isSeen(`anuncio:${id}`),
        pushPendiente,
      });
      if (!elegido || !turnoRef.current) {
        if (!anuncioRef.current) marcarEstadoDelAnuncio('libre');
        return;
      }
      // Un recorrido guiado ya está en pantalla (llegó a arrancar en otra ruta, o el
      // anuncio se decidió tarde por un cambio de push): no se le dibuja encima. El
      // turno NO se consume, así que sale en la próxima navegación.
      if (document.querySelector('.driver-popover')) {
        marcarEstadoDelAnuncio('libre');
        return;
      }
      // Se consume ANTES del `setAnuncio` y no después: entre el `await` de arriba y
      // esta línea puede haber entrado otra corrida del efecto.
      turnoRef.current = false;
      marcarEstadoDelAnuncio('mostrando');
      setAnuncio(elegido);
      // Un frame de retraso para que la transición de entrada tenga desde dónde
      // arrancar: montado y visible en el mismo frame, el modal aparece de golpe.
      requestAnimationFrame(() => vigente && setVisible(true));
    })();
    return () => { vigente = false; };
  }, [pathname, isDevTester, cargandoPush, puedePedirPush]);

  // El foco va a la cruz al abrir. Es el control que SIEMPRE existe y el que la
  // gente busca; dejar el foco donde estaba haría que el Tab siguiera recorriendo la
  // pantalla de atrás, que está tapada.
  useEffect(() => {
    if (visible) cerrarRef.current?.focus();
  }, [visible]);

  /** Saca el modal de pantalla sin anotar nada. */
  const desmontar = (irA?: string) => {
    setVisible(false);
    // Espera la transición de salida antes de desmontar.
    setTimeout(() => {
      setAnuncio(null);
      // Recién cuando terminó de salir: el recorrido que estaba esperando arranca con
      // la pantalla ya despejada.
      marcarEstadoDelAnuncio('libre');
      setErrorPush(null);
      sinContarRef.current = false;
      // La navegación va DESPUÉS de la salida: navegar con el modal todavía en
      // pantalla deja el velo encima de la pantalla nueva por 200ms.
      if (irA && irA !== pathname) navigate(irA);
    }, 200);
  };

  /**
   * El usuario lo cerró: cruz, velo, Escape, "Ahora no" o un CTA que sólo navega.
   *
   * Se anota al CERRAR y no al abrir: si se marcara al mostrarse, una recarga en el
   * medio —o un cierre de pestaña— lo daría por leído sin que el usuario lo haya
   * visto entero.
   */
  const cerrar = (irA?: string) => {
    if (anuncio?.cierre === 'reintentos') {
      if (!sinContarRef.current) registrarCancelacionDelPopup();
    } else if (anuncio) {
      markSeen(`anuncio:${anuncio.id}`, userIdRef.current);
    }
    desmontar(irA);
  };

  /**
   * El CTA del anuncio de push. El permiso SÓLO se puede pedir desde un click, así
   * que esto es lo único que puede dispararlo.
   *
   * Los tres finales son tres cosas distintas y por eso se anotan distinto:
   * activó (no hay nada que anotar, `suscripto` ya lo esconde para siempre en este
   * dispositivo), el navegador dijo que no (cuenta como un "ahora no" — y además
   * queda en `denied`, con lo cual tampoco volvería), o se rompió algo nuestro (no
   * cuenta, y se lo decimos ahí mismo en vez de cerrarle el modal en la cara).
   */
  const activarDesdeElAnuncio = async () => {
    /** Se rompió algo nuestro: no cuenta, y se lo decimos sin cerrarle el modal. */
    const seRompio = () => {
      sinContarRef.current = true;
      setErrorPush('No pudimos activarlas ahora. Probá de nuevo en un rato.');
    };

    setErrorPush(null);
    setActivando(true);
    try {
      const r = await activar();
      if (r.ok) {
        desmontar();
        return;
      }
      if (r.motivo === 'permiso-denegado') {
        registrarCancelacionDelPopup();
        desmontar();
        return;
      }
      seRompio();
    } catch {
      // `activar()` promete devolver el error en vez de tirarlo, pero si algo se
      // rompe igual —el navegador tirando en `requestPermission`, el refresco de
      // estado de después— acá termina en lo mismo que un backend caído: no cuenta
      // como "ahora no".
      seRompio();
    } finally {
      // El `finally` es lo único que impide dejar el modal trabado en "Activando…":
      // con `activando` en true la cruz, el velo, Escape y los dos botones están
      // deshabilitados, y la única salida sería recargar la página.
      setActivando(false);
    }
  };

  useEffect(() => {
    if (!anuncio) return;
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !activando) cerrar(); };
    document.addEventListener('keydown', tecla);
    return () => document.removeEventListener('keydown', tecla);
  }, [anuncio, activando]);

  if (!anuncio) return null;
  const Visual = VISUALES[anuncio.visual];

  return createPortal(
    <div
      className={`fixed inset-0 z-[900] flex items-end justify-center bg-black/65 p-0 backdrop-blur-[2px] transition-opacity duration-200 sm:items-center sm:p-4 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      // Click en el velo = cerrar, como cualquier modal no bloqueante. Un anuncio no
      // pide una decisión: no tiene por qué atrapar al usuario.
      //
      // La excepción es mientras se está activando: ahí hay un pedido en vuelo y
      // cerrar contaría un "ahora no" por algo que la persona justamente aceptó.
      onClick={() => !activando && cerrar()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="anuncio-titulo"
        onClick={(e) => e.stopPropagation()}
        // En teléfono entra desde abajo como una hoja y ocupa el ancho completo; de
        // `sm:` para arriba es una tarjeta centrada de 420px. Es el mismo par que usan
        // las referencias: hoja en iOS, diálogo en escritorio.
        className={`relative w-full max-w-[480px] overflow-hidden rounded-t-3xl bg-white shadow-[0_24px_70px_rgba(2,6,43,0.28)] transition-all duration-200 ease-out sm:rounded-3xl ${
          visible ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-4 opacity-0 sm:translate-y-0 sm:scale-[0.97]'
        }`}
      >
        <Visual />

        {/* La cruz flota SOBRE el visual, arriba a la derecha. Blanca translúcida y
            no un glifo pelado: sobre un fondo con degradé y estrellas, un ícono sin
            superficie propia desaparece en las zonas claras.

            `z-20` NO es decorativo: el contenido del visual es un `flex h-full` con
            `z-10` que cubre TODO el alto de la cabecera, esquina superior derecha
            incluida. Sin z-index propio, la cruz se dibujaba encima (viene después en
            el DOM) pero los clicks se los comía esa fila — el botón se veía perfecto
            y estaba muerto. Se detectó clickeando de verdad, no mirando la captura. */}
        <button
          ref={cerrarRef}
          type="button"
          onClick={() => cerrar()}
          disabled={activando}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-20 grid h-8 w-8 place-items-center rounded-full bg-black/30 text-white ring-1 ring-inset ring-white/25 backdrop-blur-sm transition-colors hover:bg-black/45 focus:outline-none focus:ring-2 focus:ring-white/70"
        >
          <span className="material-symbols-rounded text-[18px]">close</span>
        </button>

        {/* `pb` con el safe area sumado: en mobile la hoja llega al borde físico de la
            pantalla, y sin esto el indicador de home de un iPhone se apoya justo
            encima de "Ahora no". En escritorio `env()` vale 0 y queda el pb-6 de
            siempre. */}
        <div
          className="px-6 pt-5"
          style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))' }}
        >
          {anuncio.etiqueta && (
            <span className="mb-2.5 inline-flex h-[22px] items-center rounded-full bg-[#EBEFFE] px-2.5 text-[11px] font-bold uppercase tracking-[0.06em] text-brand-primary">
              {anuncio.etiqueta}
            </span>
          )}
          <h2
            id="anuncio-titulo"
            className="text-[21px] font-bold leading-[1.2] tracking-[-0.02em] text-black/90"
          >
            {anuncio.titulo}
          </h2>
          <p className="mt-2 text-[13.5px] leading-[19px] text-black/55">{anuncio.cuerpo}</p>

          {anuncio.puntos && anuncio.puntos.length > 0 && (
            // Los tres pasos, no tres beneficios: lo que frena a alguien frente a un
            // programa de referidos no es si le conviene —eso ya lo dijo el visual—
            // sino cuánto trabajo le va a costar. Tres líneas cortas contestan eso.
            <ul className="mt-4 flex flex-col gap-2.5">
              {anuncio.puntos.map((p) => (
                <li key={p.texto} className="flex items-center gap-2.5">
                  <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[#EBEFFE] text-brand-primary">
                    <span className="material-symbols-rounded text-[16px]">{p.icono}</span>
                  </span>
                  <span className="text-[13px] leading-[18px] text-black/70">{p.texto}</span>
                </li>
              ))}
            </ul>
          )}

          {errorPush && (
            // Arriba del botón y no debajo: es lo que explica por qué el botón ahora
            // dice "Probar de nuevo". Abajo se lee después de haber decidido.
            <p
              role="status"
              className="mt-4 rounded-lg bg-[#FEF3E2] px-3 py-2 text-[12.5px] leading-[17px] text-[#B45309]"
            >
              {errorPush}
            </p>
          )}

          <button
            type="button"
            onClick={() =>
              anuncio.cta.accion === 'activar-push'
                ? void activarDesdeElAnuncio()
                : cerrar(anuncio.cta.ruta)
            }
            disabled={activando}
            aria-busy={activando}
            className="mt-5 flex h-11 w-full items-center justify-center rounded-xl bg-brand-primary text-[14px] font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-70"
          >
            {activando
              ? 'Activando…'
              : errorPush
                ? 'Probar de nuevo'
                : anuncio.cta.label}
          </button>
          {anuncio.secundario && (
            // Sin aro ni superficie: es una salida, no una opción. Con tratamiento de
            // botón competiría con el CTA y volvería la decisión un 50/50.
            <button
              type="button"
              onClick={() => cerrar()}
              disabled={activando}
              className="mt-1 h-9 w-full text-[13px] font-[500] text-black/45 transition-colors hover:text-black/70 disabled:opacity-50"
            >
              {anuncio.secundario}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default AnuncioModal;
